/* -------------------------------------------------------------
 *  src/services/imageCrud.js  –  fully self-contained module
 * ------------------------------------------------------------- */

import { uploadData, remove } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getCurrentUserId } from './database';
import proxyapi from './ProxyApi';
import { proxyImageUrl } from '../config/api';

/* --------------------------------------------------
 * helper: calculate image dimensions & aspect ratio
 * -------------------------------------------------- */
const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
      const divisor = gcd(width, height);
      const aspectRatio = `${width / divisor}:${height / divisor}`;

      resolve({ width, height, aspectRatio });
    };

    img.onerror = () => reject(new Error('Failed to read image dimensions'));

    img.src = URL.createObjectURL(file);
  });
};

/* --------------------------------------------------
 * proxy helper – never expose S3 URL to the client
 * -------------------------------------------------- */
export const getProxyUrl = proxyImageUrl; // re-export for backward compatibility

/* --------------------------------------------------
 * upload image – store only immutable S3 key in DB
 * -------------------------------------------------- */
export const uploadImage = async (file, projectId = null) => {
  try {
    const { width, height, aspectRatio } = await getImageDimensions(file);
    const { identityId } = await fetchAuthSession();
    const userId = await getCurrentUserId();

    // unique key → "private/{identity}/{timestamp}_{filename}"
    const path = `private/${identityId}/${Date.now()}_${file.name}`;

    // 1️⃣  binary upload
    await uploadData({ path, data: file, options: { contentType: file.type } }).result;

    // 2️⃣  save metadata
    const payload = {
      userId,
      projectId,
      fileName: file.name,
      s3Path: path,
      contentType: file.type,
      size: file.size,
      width,
      height,
      aspectRatio
    };

    const res = await proxyapi.post('/api/v1/images', payload);
    return res.data;
  } catch (err) {
    /* Axios throws for HTTP 4xx/5xx – look for moderation rejection */
    const rejected = err?.response?.data?.moderationRejected;
    if (rejected) {
      throw new Error('Image rejected by moderation');
    }
    throw err; // real network / server error
  }
};

/* --------------------------------------------------
 * get image URL (returns proxy URL, not S3)
 * -------------------------------------------------- */
export const getImageUrl = async (imageId) => {
  try {
    const meta = await proxyapi.get(`/api/v1/images/${imageId}`);
    const userId = await getCurrentUserId();
    return getProxyUrl(meta.data._id || meta.data.id || imageId, userId);
  } catch (err) {
    console.error('Error getting image URL:', err);
    throw err;
  }
};

/* --------------------------------------------------
 * delete image – remove from S3 then DB
 * -------------------------------------------------- */
export const deleteImage = async (imageId) => {
  try {
    // 1️⃣ get metadata (only if we still think it exists)
    let s3Path = null;
    try {
      const meta = await proxyapi.get(`/api/v1/images/${imageId}`);
      s3Path = meta.data.s3Path;
    } catch (_) {
      /* ignore – row could already be gone */
    }

    // 2️⃣ delete the binary in S3 if we know the path
    if (s3Path) await remove({ path: s3Path });

    // 3️⃣ delete the Mongo row – silent if already gone
    await proxyapi.delete(`/api/v1/images/${imageId}`).catch((err) => {
      const code = err.response?.status;
      if (code === 204 || code === 404) return; // ok → already deleted
      throw err; // real error (5xx etc.)
    });

    return true;
  } catch (err) {
    console.error('Error deleting image:', err);
    throw err;
  }
};

/* --------------------------------------------------
 * list images (optionally by project &/or user) – now guarantees id & _id
 * -------------------------------------------------- */
export const listImages = async (projectId = null, userId = null) => {
  try {
    let url = '/api/v1/images';
    const params = new URLSearchParams();

    if (userId) params.append('userId', userId);
    if (projectId && projectId !== 'all') params.append('projectId', projectId);

    if (params.toString()) url += `?${params.toString()}`;

    const { data } = await proxyapi.get(url);

    return data.images.map((img) => {
      const normalizedId = img.id || img._id;
      return {
        ...img,
        id: normalizedId,
        _id: normalizedId //  <- add _id if missing so UI remains intact
      };
    });
  } catch (err) {
    console.error('Error listing images:', err);
    throw err;
  }
};
